// AI Career Mentor — chat page with Claude Sonnet 4.5.
// • Anonymous users → ephemeral session_id, no history persists
// • Logged-in users → history persists in MongoDB, auto-pulls test results as context
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Send, Loader2, RotateCcw, Bot, User as UserIcon, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'learnixta_mentor_session_id';

function newSessionId(user) {
  if (user && user.user_id) return `user-${user.user_id}`;
  const id = `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

const STARTERS = [
  'What should I do after 10th / Inter?',
  'Which B.Tech branch fits me?',
  'Show me AI careers with low risk',
  'I have ₹5,000 — what business can I start?',
  'Help me pick a specialization',
];

export default function AIMentor() {
  const { user } = useAuth();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);

  // Resolve session id (depends on auth state)
  useEffect(() => {
    if (user === null) return; // still checking
    if (user) {
      setSessionId(`user-${user.user_id}`);
    } else {
      const existing = localStorage.getItem(SESSION_KEY);
      setSessionId(existing || newSessionId(false));
    }
  }, [user]);

  // Load history once we know the user
  useEffect(() => {
    if (!user || !sessionId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mentor/history`, { withCredentials: true });
        const mine = (data.conversations || []).find((c) => c.session_id === sessionId);
        if (mine && Array.isArray(mine.messages) && mine.messages.length > 0) {
          setMessages(mine.messages.map((m) => ({ role: m.role, content: m.content })));
        }
      } catch {
        // ignore
      }
    })();
  }, [user, sessionId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending || !sessionId) return;
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setInput('');
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/mentor/chat`,
        {
          session_id: sessionId,
          message,
          user_context: { current_path: location.pathname },
        },
        { withCredentials: true }
      );
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply || '…' }]);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setMessages((prev) => [...prev, { role: 'assistant', content: `*(Mentor temporarily unavailable: ${detail})*` }]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (!sessionId) return;
    setMessages([]);
    try {
      await axios.post(`${API}/mentor/reset`, { session_id: sessionId }, { withCredentials: true });
    } catch {
      // ignore
    }
    if (!user) {
      const fresh = newSessionId(false);
      setSessionId(fresh);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar />
      <div className="pt-24 pb-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Ask AI Mentor</h1>
                <p className="text-xs text-gray-500">Powered by Claude Sonnet 4.5 · personalised for {user ? user.name : 'you'}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reset} data-testid="mentor-reset-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> New chat
            </Button>
          </div>

          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden" data-testid="mentor-chat-card">
            <div
              ref={scrollRef}
              className="h-[60vh] overflow-y-auto px-5 py-6 space-y-4"
              data-testid="mentor-messages"
            >
              {messages.length === 0 && (
                <div className="text-center py-8" data-testid="mentor-empty-state">
                  <Sparkles className="w-10 h-10 text-teal-500 mx-auto mb-3" />
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    {user ? `Hi ${user.name}!` : 'Hi there!'}
                  </h2>
                  <p className="text-gray-600 mb-6">Ask me anything about your career path.</p>
                  <div className="flex flex-wrap justify-center gap-2 max-w-md mx-auto">
                    {STARTERS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-full transition-colors"
                        data-testid={`mentor-starter-${s.slice(0, 12).replace(/\s+/g, '-')}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} />
              ))}

              {sending && (
                <div className="flex gap-3" data-testid="mentor-typing">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-gray-200 p-4 flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your career path…"
                className="flex-1 h-11"
                disabled={sending}
                data-testid="mentor-input"
              />
              <Button
                type="submit"
                disabled={sending || !input.trim()}
                className="btn-primary h-11 px-5"
                data-testid="mentor-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>

          <p className="text-xs text-center text-gray-400 mt-2" data-testid="mentor-ai-disclaimer">
            Lexi can make mistakes — reverify outside too.
          </p>

          {!user && (
            <p className="text-xs text-center text-gray-500 mt-3" data-testid="mentor-signin-hint">
              <a href="/signin" className="text-teal-600 hover:underline">Sign in</a> to save this chat and get advice personalised to your test results.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
          isUser ? 'bg-blue-100' : 'bg-teal-100'
        }`}
      >
        {isUser ? <UserIcon className="w-4 h-4 text-blue-600" /> : <Bot className="w-4 h-4 text-teal-600" />}
      </div>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? 'bg-teal-600 text-white rounded-tr-sm'
            : 'bg-gray-100 text-gray-900 rounded-tl-sm prose prose-sm max-w-none prose-a:text-teal-600 prose-a:font-medium prose-headings:mt-3 prose-headings:mb-1 prose-p:my-2 prose-ul:my-2 prose-strong:text-gray-900'
        }`}
      >
        {isUser ? content : <ReactMarkdown>{content}</ReactMarkdown>}
      </div>
    </div>
  );
}
